import React, { useEffect, useState, useRef } from "react";
import GhostContentAPI from "@tryghost/content-api";

function timeAgo(dateStr) {
	if (!dateStr) return "";
	const then = new Date(dateStr).getTime();
	const now = Date.now();
	const diff = Math.floor((now - then) / 1000);
	if (diff < 60) return `${diff}s ago`;
	if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
	if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
	const days = Math.floor(diff / 86400);
	if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
	const weeks = Math.floor(days / 7);
	if (weeks < 4) return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
	return new Date(dateStr).toLocaleDateString();
}

export default function BlogsVisible({ tagFilter = "blogs", limit = 20 }) {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const scrollRef = useRef(null);

	// compute a sensible scroll amount (card width + gap) for moving one card at a time
	function getScrollAmount() {
		const sc = scrollRef.current;
		if (!sc) return Math.round((sc ? sc.clientWidth : 0) * 0.8) || 300;
		const inner = sc.querySelector('.flex');
		if (!inner) return Math.round(sc.clientWidth * 0.8) || 300;
		const first = inner.querySelector('article');
		if (!first) return Math.round(sc.clientWidth * 0.8) || 300;
		const articleWidth = first.clientWidth || 300;
		// try to read the gap between flex children; fallback to 24
		const style = window.getComputedStyle(inner);
		const gapStr = style.gap || style.columnGap || style.rowGap || '';
		const gap = gapStr ? parseInt(gapStr, 10) || 0 : 24;
		return articleWidth + gap;
	}

	// handle keyboard arrow navigation when the scroll container is focused
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;

		function onKey(e) {
			if (e.key === "ArrowRight") {
				e.preventDefault();
				const amount = getScrollAmount();
				el.scrollBy({ left: amount, behavior: "smooth" });
			} else if (e.key === "ArrowLeft") {
				e.preventDefault();
				const amount = getScrollAmount();
				el.scrollBy({ left: -amount, behavior: "smooth" });
			}
		}

		el.addEventListener("keydown", onKey);
		return () => el.removeEventListener("keydown", onKey);
	}, []);

	// center the first card when posts load or on resize so mobile shows centered card
	useEffect(() => {
		function centerFirst() {
			const sc = scrollRef.current;
			if (!sc) return;
			const inner = sc.querySelector('.flex');
			const first = inner ? inner.querySelector('article') : null;
			if (!first) return;
			const left = first.offsetLeft - (sc.clientWidth - first.clientWidth) / 2;
			sc.scrollTo({ left, behavior: 'smooth' });
		}

		// run after small delay to allow layout to settle
		const t = setTimeout(centerFirst, 120);
		window.addEventListener('resize', centerFirst);
		return () => {
			clearTimeout(t);
			window.removeEventListener('resize', centerFirst);
		};
	}, [loading, posts]);

	useEffect(() => {
		let mounted = true;
		try {
			const api = new GhostContentAPI({
				url: process.env.NEXT_PUBLIC_GHOST_API_URL || "https://ghost.neetsupport.com",
				key: process.env.NEXT_PUBLIC_GHOST_API_KEY || "",
				version: "v3.0",
			});

			api.posts
				.browse({
					limit,
					include: "authors,tags",
					filter: `tag:${tagFilter}`,
					order: "published_at DESC",
				})
				.then((res) => {
					if (!mounted) return;
					setPosts(res.slice(0, limit));
				})
				.catch((err) => {
					console.warn("Failed to load posts for BlogsVisible:", err);
					if (!mounted) return;
					setPosts([]);
				})
				.finally(() => mounted && setLoading(false));
		} catch (e) {
			console.warn("Error initializing Ghost client", e);
			setLoading(false);
			setPosts([]);
		}

		return () => {
			mounted = false;
		};
	}, [tagFilter, limit]);

	return (
		<section className="w-full mx-auto px-4 sm:px-6 lg:px-16 ">
			<div className="text-center mb-8">
				<h2 className="text-2xl sm:text-3xl font-bold">Top Blogs for Smarter <span className="text-[#ff9245]">Counseling Decisions</span> </h2>
				<p className="text-sm sm:text-lg text-gray-500 mt-2">Everything you need to know about NEET PG 2026 and how it impacts your medical career.</p>
			</div>

			<div className="relative overflow-visible">
				{/* horizontal scroll area (focusable for keyboard arrow navigation) */}
				<div
					ref={scrollRef}
					tabIndex={0}
					className="overflow-x-auto no-scrollbar py-8 overflow-visible"
					style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
				>
					<div className="flex items-stretch gap-6 px-12">
						{loading
							? Array.from({ length: Math.min(4, limit) }).map((_, i) => (
								<article key={i} className="w-72 sm:w-72 md:w-80 lg:w-80 flex-shrink-0 h-72" style={{ scrollSnapAlign: 'center' }}>
									<div className="rounded-lg overflow-hidden bg-white shadow-sm animate-pulse h-full flex flex-col">
										<div className="w-full h-44 bg-gray-200 flex-shrink-0" />
										<div className="p-3 space-y-2 flex-grow">
											<div className="h-4 bg-gray-200 rounded w-3/4" />
											<div className="h-3 bg-gray-200 rounded w-1/2" />
										</div>
									</div>
								</article>
							))
							: posts.length > 0
								? posts.map((v, idx) => {
									const id = v.id || v.slug || idx;
									const title = v.title || "Untitled";
									const thumb = v.feature_image ? v.feature_image.replace(/ghost\.neetsupport\.com/g, "neetsupport.com") : null;
									const excerpt = (v.excerpt || v.custom_excerpt || "").replace(/(<([^>]+)>)/gi, "").slice(0, 140);
									const tagNames = (v.tags || []).slice(0, 2).map(t => t.name).filter(Boolean);
									const time = timeAgo(v.published_at);

									return (
										<article key={id} className="w-72 sm:w-72 md:w-80 lg:w-96 flex-shrink-0 h-80 md:h-96 lg:h-96" style={{ scrollSnapAlign: 'center' }}>
											<a
												href={`/blogs/${v.slug}`}
												className="group block rounded-2xl overflow-hidden bg-white shadow-lg focus:outline-none h-full flex flex-col"
											>
												<div className="relative h-44 md:h-48 lg:h-52 bg-gray-100">
													{thumb ? (
														<img loading="lazy" src={thumb} alt={title} className="w-full h-full object-cover" />
													) : (
														<div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
															<svg className="w-12 h-12 text-gray-300" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
																<path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14l4-3 3 3 5-4 6 4z" />
															</svg>
														</div>
													)}
													{/* gradient overlay for readable title */}
													<div className="absolute left-0 right-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
													{/* top-left badges */}
													<div className="absolute top-3 left-3 flex gap-2">
														{tagNames.map((t, i) => (
															<span key={i} className="bg-white/90 text-xs text-gray-700 px-2 py-1 rounded-full font-medium shadow-sm">{t}</span>
														))}
													</div>
												</div>
												<div className="p-4 md:p-5 flex-grow">
													<h3 className="text-sm md:text-base font-semibold text-gray-900 leading-tight line-clamp-2">{title}</h3>
													<p className="mt-2 text-xs md:text-sm text-gray-600 line-clamp-2">{excerpt}</p>
													<div className="mt-3 flex items-center justify-between">
														<span className="text-xs text-gray-400">{time}</span>
														<span className="text-xs text-[#ff9245] font-semibold">Read more →</span>
													</div>
												</div>
											</a>
										</article>
									);
								})
								: (
									<div className="text-gray-500 px-4">No blog posts found.</div>
								)}
					</div>
				</div>

				{/* left / right clickable arrows */}
				<button
					aria-label="Scroll left"
					onClick={() => {
						if (!scrollRef.current) return;
						const amount = getScrollAmount();
						scrollRef.current.scrollBy({ left: -amount, behavior: "smooth" });
					}}
					className="flex absolute left-0 sm:-left-10 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-[#ff9245] ring-1 ring-[#ff9245] rounded-full p-3 sm:p-2 shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-[#ff9245] transition-transform active:scale-95"
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
					</svg>
				</button>
				<button
					aria-label="Scroll right"
					onClick={() => {
						if (!scrollRef.current) return;
						const amount = getScrollAmount();
						scrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
					}}
					className="flex absolute right-0 sm:-right-10 top-1/2 -translate-y-1/2 z-30 bg-white/90 hover:bg-white text-[#ff9245] ring-1 ring-[#ff9245] rounded-full p-3 sm:p-2 shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-[#ff9245] transition-transform active:scale-95"
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
					</svg>
				</button>
			</div>

			<div className="text-center mt-8">
				<a
					href="/blogs"
					className="inline-flex items-center gap-3 bg-[#ff9245] text-white font-semibold px-5 py-2 rounded-full shadow-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff9245]"
					aria-label="Explore all blogs"
				>
					<span>Explore all blogs</span>
					<svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
					</svg>
				</a>
			</div>

			{/* hide native scrollbar for the horizontal list across browsers */}
			<style jsx>{`
				.no-scrollbar {
					-ms-overflow-style: none; /* IE and Edge */
					scrollbar-width: none; /* Firefox */
				}
				.no-scrollbar::-webkit-scrollbar {
					display: none; /* WebKit */
				}
			`}</style>
		</section>
	);
}
